import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Host,
  Inject,
  input,
  InputSignal,
  Optional,
  output,
  OutputEmitterRef,
  Self,
} from '@angular/core';
import {
  WLCM_FORM_CONTROL,
  WLCM_FORM_CONTROL_PROVIDER,
  WlcmFormControl,
  WlcmFormsModule,
  WlcmPlacesApi,
} from '@wlcm/angular/forms';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { WlcmAutocompleteOption, WlcmOption } from '@wlcm/angular/core';
import { FamilyMemberForm } from '../../models/family-member-form.models';
import { ParentFormModel, ParentFormPayload, ParentFormValue } from '../../models/parent-form.models';
import { WlcmPhoneInputModule } from '@wlcm/angular/phone-input';
import { Parent } from '@core/models/entities/parent.models';
import { MatSelectChange } from '@angular/material/select';
import { ParentApi } from '@core/services/api/parent.api';
import { patchSelectableParentForm, transformToParentPayload } from '../../utils';
import { map, Observable } from 'rxjs';
import { ADMIN_ROLES } from '@core/constants';
import { CanRenderDirective } from '@shared/directives/can-render.directive';
import { WlcmPaginatedData, WlcmQueryParams } from '@wlcm/common';
import { TrackReactiveControlDirective } from '@shared/modules/form-tracker';
import { Location } from '@core/models/entities/location.models';
import { LocationApi } from '@core/services/api/location.api';
import { NgIf } from '@angular/common';

@Component({
  selector: 'napa-parent-form',
  standalone: true,
  imports: [
    WlcmFormsModule,
    WlcmPhoneInputModule,
    ReactiveFormsModule,
    CanRenderDirective,
    TrackReactiveControlDirective,
    NgIf,
  ],
  templateUrl: './parent-form.component.html',
  styleUrl: './parent-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WLCM_FORM_CONTROL_PROVIDER],
})
export class ParentFormComponent extends FamilyMemberForm<ParentFormModel, ParentFormPayload> {
  selected: OutputEmitterRef<Parent> = output();

  type: InputSignal<string> = input<string>('primary');

  parentOptions: InputSignal<WlcmOption[]> = input<WlcmOption[]>([]);

  yesNoOptions: WlcmOption[] = [new WlcmOption(true, 'Yes'), new WlcmOption(false, 'No')];

  protected readonly AdminRoles = ADMIN_ROLES;

  constructor(
    private parentApi: ParentApi,
    private placesApi: WlcmPlacesApi,
    protected override changeDetector: ChangeDetectorRef,
    @Inject(WLCM_FORM_CONTROL) protected override wlcmFormControl: WlcmFormControl,
    @Optional() @Host() @Self() protected override controlContainer: ControlContainer,
    private locationApi: LocationApi
  ) {
    super(changeDetector, wlcmFormControl, controlContainer);
  }

  get payload(): ParentFormPayload {
    return transformToParentPayload(this.form.value as ParentFormValue);
  }

  override get storePayload(): ParentFormValue {
    return this.form.value as ParentFormValue;
  }

  get isValid(): boolean {
    return this.form.valid;
  }

  get fullNameControl(): FormControl {
    return this.form.controls.full_name;
  }

  handleSelectedParent(option: MatSelectChange): void {
    const parent: Parent = option.value.data;

    this.selected.emit(parent);

    return patchSelectableParentForm(this.form, parent, false);
  }

  markAllAsTouched(): void {
    this.form.markAllAsTouched();

    this.changeDetector.markForCheck();
  }

  getParents = (params: WlcmQueryParams): Observable<WlcmPaginatedData<WlcmAutocompleteOption>> => {
    return this.parentApi.getPaginated(params).pipe(
      map((paginatedData: WlcmPaginatedData<Parent>) => {
        return {
          ...paginatedData,
          data: paginatedData.data.map(
            (parent: Parent) => new WlcmAutocompleteOption(parent._id, parent.full_name, parent.full_name, parent)
          ),
        };
      })
    );
  };

  fetchLocationOptions = (params: WlcmQueryParams): Observable<WlcmPaginatedData<WlcmAutocompleteOption>> => {
    return this.locationApi.getPaginated(params).pipe(
      map((paginated: WlcmPaginatedData<Location>) => {
        return {
          ...paginated,
          data: paginated.data.map((location: Location) => this.locationToOption(location)),
        };
      })
    );
  };

  private locationToOption = (location: Location): WlcmAutocompleteOption => {
    return new WlcmAutocompleteOption(location._id, location.name, location.name, location);
  };

  getPlaces = (params: WlcmQueryParams): Observable<WlcmPaginatedData<WlcmAutocompleteOption>> => {
    return this.placesApi.getPlacesPaginated(params.query || '');
  };

  validateControlContainer(controlContainer: ControlContainer): void {
    if (!controlContainer || !(controlContainer?.control instanceof FormGroup)) {
      throw new Error('FormGroup<ParentFormModel> is not provided.');
    }
  }
}
